// ============================================================
//  GFM3 — ExportPointage.js
//  src/components/ExportPointage.js
//
//  Usage dans PointagePage.js :
//    import ExportPointage from '../components/ExportPointage';
//    <ExportPointage data={feuille} date={dateJour} />
//    <ExportPointage data={statsData} type="stats" periode={{debut, fin}} />
//    <ExportPointage data={agentData} type="agent" />
// ============================================================

import React, { useState } from 'react';
import * as XLSX from 'xlsx';

// ── Helpers ──────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, j] = d.split('-');
  return `${j}/${m}/${y}`;
};
const fmtTime = (t) => (t ? t.slice(0, 5) : '');
const fmtPct  = (v) => (v != null ? `${v}%` : '');

const STATUT_LABELS = {
  Present: 'Présent', Absent: 'Absent', Retard: 'Retard',
  'Demi-journee': 'Demi-journée', Conge: 'Congé', Mission: 'Mission',
  'Non saisi': 'Non saisi',
};

// ── Styles communs pour les cellules d'en-tête ──────────────
const HEADER_STYLE = {
  font:      { bold: true, color: { argb: 'FFFFFFFF' }, sz: 11 },
  fill:      { fgColor: { argb: 'FF1565C0' }, patternType: 'solid' },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top:    { style: 'thin', color: { argb: 'FFB0BEC5' } },
    bottom: { style: 'thin', color: { argb: 'FFB0BEC5' } },
    left:   { style: 'thin', color: { argb: 'FFB0BEC5' } },
    right:  { style: 'thin', color: { argb: 'FFB0BEC5' } },
  },
};
const ROW_STYLE = (even) => ({
  fill:      { fgColor: { argb: even ? 'FFF3F7FB' : 'FFFFFFFF' }, patternType: 'solid' },
  alignment: { vertical: 'center' },
  border: {
    bottom: { style: 'hair', color: { argb: 'FFE0E8F0' } },
    left:   { style: 'thin', color: { argb: 'FFE0E8F0' } },
    right:  { style: 'thin', color: { argb: 'FFE0E8F0' } },
  },
});
const STATUT_COLOR = {
  Present:        'FF2e7d32',
  Absent:         'FFc62828',
  Retard:         'FFf57f17',
  'Demi-journee': 'FF1565C0',
  Conge:          'FF6a1b9a',
  Mission:        'FF006064',
  'Non saisi':    'FF757575',
};

// ── Ajouter un titre stylisé à une feuille ─────────────────
function addTitle(ws, title, subtitle, colSpan) {
  // Titre
  ws['A1'] = { v: title, t: 's', s: {
    font:      { bold: true, sz: 14, color: { argb: 'FF1a3a5c' } },
    alignment: { horizontal: 'left' },
  }};
  ws['!merges'] = ws['!merges'] || [];
  ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: colSpan - 1 } });

  // Sous-titre
  ws['A2'] = { v: subtitle, t: 's', s: {
    font:      { sz: 10, color: { argb: 'FF6b7894' } },
    alignment: { horizontal: 'left' },
  }};
  ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: colSpan - 1 } });

  // Ligne vide
  ws['A3'] = { v: '', t: 's' };
}

// ════════════════════════════════════════════════════════════
//  EXPORT 1 — Feuille de présence journalière
// ════════════════════════════════════════════════════════════
function exportFeuille(data, date, edits) {
  const wb  = XLSX.utils.book_new();
  const rows = data?.data || [];

  // ── Feuille principale ───────────────────────────────────
  const wsData = [];

  // En-têtes
  const headers = [
    'Matricule', 'Nom', 'Prénoms', 'Département', 'Fonction',
    'Statut', 'Heure arrivée', 'Heure départ', 'Observation',
  ];
  wsData.push(headers);

  // Données
  rows.forEach(row => {
    const e = edits?.[row.id] || {};
    const statut = e.statut || row.statut || 'Non saisi';
    wsData.push([
      row.matricule,
      row.nom,
      row.prenoms,
      row.departement || '',
      row.fonction    || '',
      STATUT_LABELS[statut] || statut,
      fmtTime(e.heure_arrivee || row.heure_arrivee),
      fmtTime(e.heure_depart  || row.heure_depart),
      e.observation || row.observation || '',
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Largeurs colonnes
  ws['!cols'] = [
    { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 18 },
    { wch: 20 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 30 },
  ];

  // Style en-têtes (ligne 1 = index 0)
  headers.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: i });
    if (!ws[cell]) ws[cell] = { v: headers[i], t: 's' };
    ws[cell].s = HEADER_STYLE;
  });

  // Style lignes données + couleur statut
  rows.forEach((row, ri) => {
    const e    = edits?.[row.id] || {};
    const stat = e.statut || row.statut || 'Non saisi';
    const rIdx = ri + 1; // décalé de 1 (en-tête)
    const even = ri % 2 === 1;

    for (let c = 0; c < headers.length; c++) {
      const cell = XLSX.utils.encode_cell({ r: rIdx, c });
      if (!ws[cell]) ws[cell] = { v: '', t: 's' };
      ws[cell].s = ROW_STYLE(even);
    }
    // Colorier la cellule Statut
    const statCell = XLSX.utils.encode_cell({ r: rIdx, c: 5 });
    if (ws[statCell]) {
      ws[statCell].s = {
        ...ROW_STYLE(even),
        font: { bold: true, color: { argb: STATUT_COLOR[stat] || 'FF333333' } },
      };
    }
  });

  XLSX.utils.book_append_sheet(wb, ws, 'Présences');

  // ── Feuille Récap KPI ────────────────────────────────────
  const counts  = data?.counts  || {};
  const kpiData = [
    ['Indicateur',                'Valeur'],
    ['Date',                      fmtDate(date)],
    ['Effectif total',            data?.total ?? 0],
    ['Taux de présence',          fmtPct(data?.taux_presence)],
    ['Présents',                  counts.Present         ?? 0],
    ['Absents',                   counts.Absent          ?? 0],
    ['Retards',                   counts.Retard          ?? 0],
    ['Demi-journées',             counts['Demi-journee'] ?? 0],
    ['Congés',                    counts.Conge           ?? 0],
    ['Missions',                  counts.Mission         ?? 0],
    ['Non saisis',                counts['Non saisi']    ?? 0],
    ['Généré le',                 new Date().toLocaleString('fr-CI')],
  ];
  const wsKpi = XLSX.utils.aoa_to_sheet(kpiData);
  wsKpi['!cols'] = [{ wch: 22 }, { wch: 18 }];
  // Style en-tête
  ['A1','B1'].forEach(ref => {
    if (!wsKpi[ref]) wsKpi[ref] = {};
    wsKpi[ref].s = HEADER_STYLE;
  });
  XLSX.utils.book_append_sheet(wb, wsKpi, 'Récap KPI');

  // Télécharger
  XLSX.writeFile(wb, `Pointage_${date}.xlsx`);
}

// ════════════════════════════════════════════════════════════
//  EXPORT 2 — Statistiques globales
// ════════════════════════════════════════════════════════════
function exportStats(statsData, debut, fin) {
  const wb = XLSX.utils.book_new();

  // ── Feuille Récap individuel ─────────────────────────────
  const indHeaders = [
    'Agent', 'Département', 'Présents', 'Absences', 'Retards',
    'Congés', 'Missions', 'Demi-journées', 'Jours saisis', 'Taux présence',
  ];
  const indRows = (statsData.individuel || []).map(a => [
    a.employe, a.departement || '',
    a.presents, a.absences, a.retards, a.conges, a.missions,
    a.demi_journees ?? 0,
    a.jours_saisis, fmtPct(a.taux_presence),
  ]);

  const wsInd = XLSX.utils.aoa_to_sheet([indHeaders, ...indRows]);
  wsInd['!cols'] = [
    { wch: 28 }, { wch: 18 }, { wch: 10 }, { wch: 10 },
    { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 },
    { wch: 12 }, { wch: 14 },
  ];
  indHeaders.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: i });
    if (!wsInd[cell]) wsInd[cell] = { v: indHeaders[i], t: 's' };
    wsInd[cell].s = HEADER_STYLE;
  });
  XLSX.utils.book_append_sheet(wb, wsInd, 'Récap individuel');

  // ── Feuille Évolution quotidienne ────────────────────────
  const evolHeaders = ['Date', 'Présents', 'Absents', 'Retards', 'Total saisi', 'Taux présence'];
  const evolRows = (statsData.evolution || []).map(ev => {
    const taux = ev.total_saisi > 0
      ? Math.round((ev.presents / ev.total_saisi) * 100) : 0;
    return [fmtDate(ev.jour), ev.presents, ev.absents, ev.retards, ev.total_saisi, fmtPct(taux)];
  });
  const wsEvol = XLSX.utils.aoa_to_sheet([evolHeaders, ...evolRows]);
  wsEvol['!cols'] = [{ wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
  evolHeaders.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: i });
    if (!wsEvol[cell]) wsEvol[cell] = { v: evolHeaders[i], t: 's' };
    wsEvol[cell].s = HEADER_STYLE;
  });
  XLSX.utils.book_append_sheet(wb, wsEvol, 'Évolution quotidienne');

  // ── Feuille Par département ──────────────────────────────
  const deptHeaders = ['Département', 'Nb employés', 'Présents', 'Absents', 'Retards', 'Taux présence'];
  const deptRows = (statsData.par_departement || []).map(d => [
    d.departement || 'Sans département',
    d.nb_employes, d.presents, d.absents, d.retards, fmtPct(d.taux_presence),
  ]);
  const wsDept = XLSX.utils.aoa_to_sheet([deptHeaders, ...deptRows]);
  wsDept['!cols'] = [{ wch: 22 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 }];
  deptHeaders.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: i });
    if (!wsDept[cell]) wsDept[cell] = { v: deptHeaders[i], t: 's' };
    wsDept[cell].s = HEADER_STYLE;
  });
  XLSX.utils.book_append_sheet(wb, wsDept, 'Par département');

  // ── Feuille Top absents ──────────────────────────────────
  const absHeaders = ['Agent', 'Département', 'Absences', 'Retards', 'Jours saisis', 'Taux absence'];
  const absRows = (statsData.top_absents || []).map(a => [
    a.employe, a.departement || '',
    a.nb_absences, a.nb_retards, a.nb_jours_saisis, fmtPct(a.taux_absence),
  ]);
  const wsAbs = XLSX.utils.aoa_to_sheet([absHeaders, ...absRows]);
  wsAbs['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
  absHeaders.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: i });
    if (!wsAbs[cell]) wsAbs[cell] = { v: absHeaders[i], t: 's' };
    wsAbs[cell].s = HEADER_STYLE;
  });
  XLSX.utils.book_append_sheet(wb, wsAbs, 'Top absents');

  XLSX.writeFile(wb, `Stats_Pointage_${debut}_${fin}.xlsx`);
}

// ════════════════════════════════════════════════════════════
//  EXPORT 3 — Fiche agent
// ════════════════════════════════════════════════════════════
function exportAgent(agentData) {
  const wb   = XLSX.utils.book_new();
  const emp  = agentData.employe  || {};
  const stats= agentData.stats    || {};
  const hist = agentData.historique || [];
  const evol = agentData.evolution_mois || [];

  // ── Résumé ───────────────────────────────────────────────
  const resumeData = [
    ['Champ', 'Valeur'],
    ['Agent',                 emp.employe     || ''],
    ['Matricule',             emp.matricule   || ''],
    ['Département',           emp.departement || ''],
    ['Fonction',              emp.fonction    || ''],
    ['Période début',         fmtDate(agentData.periode?.debut)],
    ['Période fin',           fmtDate(agentData.periode?.fin)],
    ['', ''],
    ['Taux de présence',      fmtPct(stats.taux_presence)],
    ['Taux d\'absence',       fmtPct(stats.taux_absence)],
    ['Jours présents',        stats.presents      ?? 0],
    ['Absences',              stats.absences      ?? 0],
    ['Retards',               stats.retards       ?? 0],
    ['Congés',                stats.conges        ?? 0],
    ['Missions',              stats.missions      ?? 0],
    ['Demi-journées',         stats.demi_journees ?? 0],
    ['Jours saisis',          stats.jours_saisis  ?? 0],
    ['Heure arrivée moy.',    fmtTime(stats.heure_arrivee_moy)],
    ['Durée journée moy.',    fmtTime(stats.duree_moy_journee)],
    ['Généré le',             new Date().toLocaleString('fr-CI')],
  ];
  const wsResume = XLSX.utils.aoa_to_sheet(resumeData);
  wsResume['!cols'] = [{ wch: 24 }, { wch: 20 }];
  ['A1','B1'].forEach(ref => {
    if (!wsResume[ref]) wsResume[ref] = {};
    wsResume[ref].s = HEADER_STYLE;
  });
  XLSX.utils.book_append_sheet(wb, wsResume, 'Résumé');

  // ── Historique détaillé ──────────────────────────────────
  const histHeaders = ['Date', 'Statut', 'Arrivée', 'Départ', 'Durée', 'Observation'];
  const histRows = hist.map(h => {
    let duree = '';
    if (h.heure_arrivee && h.heure_depart) {
      const [ah, am] = h.heure_arrivee.split(':').map(Number);
      const [dh, dm] = h.heure_depart.split(':').map(Number);
      const diff = (dh * 60 + dm) - (ah * 60 + am);
      if (diff > 0) duree = `${Math.floor(diff/60)}h${String(diff%60).padStart(2,'0')}`;
    }
    return [
      fmtDate(h.date_pointage),
      STATUT_LABELS[h.statut] || h.statut,
      fmtTime(h.heure_arrivee),
      fmtTime(h.heure_depart),
      duree,
      h.observation || '',
    ];
  });
  const wsHist = XLSX.utils.aoa_to_sheet([histHeaders, ...histRows]);
  wsHist['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 30 }];
  histHeaders.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: i });
    if (!wsHist[cell]) wsHist[cell] = { v: histHeaders[i], t: 's' };
    wsHist[cell].s = HEADER_STYLE;
  });
  // Colorier colonne statut
  histRows.forEach((row, ri) => {
    const statCell = XLSX.utils.encode_cell({ r: ri + 1, c: 1 });
    const origStat = hist[ri]?.statut || '';
    if (wsHist[statCell]) {
      wsHist[statCell].s = {
        font: { bold: true, color: { argb: STATUT_COLOR[origStat] || 'FF333333' } },
        alignment: { vertical: 'center' },
      };
    }
  });
  XLSX.utils.book_append_sheet(wb, wsHist, 'Historique');

  // ── Évolution mensuelle ──────────────────────────────────
  if (evol.length > 0) {
    const evolHeaders = ['Mois', 'Présents', 'Absences', 'Retards', 'Total', 'Taux présence'];
    const evolRows = evol.map(m => {
      const taux = m.total > 0 ? Math.round((m.presents / m.total) * 100) : 0;
      return [m.mois, m.presents, m.absences, m.retards, m.total, fmtPct(taux)];
    });
    const wsEvol = XLSX.utils.aoa_to_sheet([evolHeaders, ...evolRows]);
    wsEvol['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 }];
    evolHeaders.forEach((_, i) => {
      const cell = XLSX.utils.encode_cell({ r: 0, c: i });
      if (!wsEvol[cell]) wsEvol[cell] = { v: evolHeaders[i], t: 's' };
      wsEvol[cell].s = HEADER_STYLE;
    });
    XLSX.utils.book_append_sheet(wb, wsEvol, 'Évolution mensuelle');
  }

  const nom = emp.employe?.replace(/ /g, '_') || 'Agent';
  XLSX.writeFile(wb, `Fiche_${nom}_${agentData.periode?.debut}_${agentData.periode?.fin}.xlsx`);
}

// ════════════════════════════════════════════════════════════
//  COMPOSANT BOUTON D'EXPORT
// ════════════════════════════════════════════════════════════
export default function ExportPointage({ type = 'feuille', data, date, edits, debut, fin }) {
  const [loading, setLoading] = useState(false);

  const handleExport = () => {
    setLoading(true);
    try {
      if (type === 'feuille') {
        exportFeuille(data, date, edits);
      } else if (type === 'stats') {
        exportStats(data, debut, fin);
      } else if (type === 'agent') {
        exportAgent(data);
      }
    } catch (e) {
      console.error('Export error', e);
      alert('Erreur lors de l\'export : ' + e.message);
    }
    setLoading(false);
  };

  const labels = {
    feuille: '⬇ Export Excel',
    stats:   '⬇ Export statistiques',
    agent:   '⬇ Export fiche agent',
  };

  return (
    <button
      className="btn btn-outline-blue btn-sm"
      onClick={handleExport}
      disabled={loading || !data}
      title={`Télécharger en Excel`}
      style={{ gap: 6 }}
    >
      {loading ? (
        <><span className="spinner spinner-dark" style={{ width: 12, height: 12, borderWidth: 2 }} /> Export…</>
      ) : (
        labels[type] || '⬇ Export'
      )}
    </button>
  );
}
